import { Accordion, Text, Badge, Group, Stack, Paper } from "@mantine/core";
import {
  IconFlask,
  IconBox,
  IconMicroscope,
  IconClock,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { type CaseSpecimen } from "../../services";

function resolveGrossVariables(html: string): string {
  if (!html) return "";
  return html
    .replace(
      /<span[^>]*data-variable-value="([^"]*)"[^>]*>\{\{\w+\}\}<\/span>/g,
      "$1"
    )
    .replace(/<span[^>]*data-variable="[^"]*"[^>]*>\{\{\w+\}\}<\/span>/g, "")
    .replace(/\{\{[^}]+\}\}/g, "");
}

interface SpecimenHierarchyProps {
  specimens: CaseSpecimen[];
  variant?: "default" | "separated";
}

export default function SpecimenHierarchy({
  specimens,
  variant = "separated",
}: SpecimenHierarchyProps) {
  if (!specimens || specimens.length === 0) {
    return (
      <Paper p="md" withBorder>
        <Text c="dimmed" size="sm">
          No specimens available
        </Text>
      </Paper>
    );
  }

  return (
    <Accordion
      variant={variant}
      styles={{
        control: {
          backgroundColor: "transparent !important",
          "&:hover": {
            backgroundColor: "transparent !important",
          },
        },
        item: {
          borderRadius: "var(--mantine-radius-sm)",
        },
      }}
    >
      {specimens.map((specimen) => {
        const blocks = specimen.blocks || [];
        const totalSlides = blocks.reduce(
          (sum, b) => sum + (b.slides?.length || 0),
          0
        );

        return (
          <Accordion.Item key={specimen.id} value={specimen.id}>
            <Accordion.Control icon={<IconFlask size={20} />}>
              <Group justify="space-between">
                <Group gap="xs">
                  <Text fw={500}>Specimen {specimen.number}</Text>
                  {specimen.source && (
                    <Badge variant="light" size="sm">
                      {specimen.source.name}
                    </Badge>
                  )}
                  {specimen.subsite && (
                    <Badge variant="outline" size="sm">
                      {specimen.subsite.name}
                    </Badge>
                  )}
                </Group>
                <Group gap="xs">
                  <Text size="xs" c="dimmed">
                    {blocks.length} block{blocks.length !== 1 ? "s" : ""}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {totalSlides} slide{totalSlides !== 1 ? "s" : ""}
                  </Text>
                </Group>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                {/* Specimen Details */}
                <Stack gap="xs">
                  {specimen.collectionMethod && (
                    <Group gap="xs">
                      <Text size="sm" fw={500} w={100}>
                        Collection:
                      </Text>
                      <Text size="sm">{specimen.collectionMethod}</Text>
                    </Group>
                  )}
                  {specimen.material && (
                    <Group gap="xs">
                      <Text size="sm" fw={500} w={100}>
                        Material:
                      </Text>
                      <Text size="sm">
                        {(
                          {
                            formalin_jar: "Formalin jar",
                            block: "Block",
                            slides_only: "Slides only",
                            blood_tube: "Blood tube",
                            urine_sample: "Urine sample",
                            stool_collection: "Stool collection",
                          } as Record<string, string>
                        )[specimen.material] || specimen.material}
                      </Text>
                    </Group>
                  )}
                  {specimen.tissueDisposition && (
                    <Group gap="xs">
                      <Text size="sm" fw={500} w={100}>
                        Disposition:
                      </Text>
                      <Badge size="sm" variant="light">
                        {specimen.tissueDisposition
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (c: string) => c.toUpperCase())}
                      </Badge>
                    </Group>
                  )}
                  {specimen.ruleOuts && specimen.ruleOuts.length > 0 && (
                    <Group gap="xs" align="flex-start">
                      <Text size="sm" fw={500} w={100}>
                        Rule Outs:
                      </Text>
                      <Group gap={4}>
                        {specimen.ruleOuts.map((ruleOut, idx) => (
                          <Badge
                            key={idx}
                            size="sm"
                            variant="outline"
                            color="orange"
                          >
                            {ruleOut}
                          </Badge>
                        ))}
                      </Group>
                    </Group>
                  )}
                  {specimen.comments && (
                    <Group gap="xs" align="flex-start">
                      <Text size="sm" fw={500} w={100}>
                        Comments:
                      </Text>
                      <Text size="sm" style={{ flex: 1 }}>
                        {specimen.comments}
                      </Text>
                    </Group>
                  )}
                  {specimen.grossedBy && (
                    <Group gap="xs">
                      <Text size="sm" fw={500} w={100}>
                        Grossed by:
                      </Text>
                      <Text size="sm">{specimen.grossedBy.fullName}</Text>
                    </Group>
                  )}
                  {specimen.grossedAt && (
                    <Group gap="xs">
                      <Text size="sm" fw={500} w={100}>
                        <Group gap={4}>
                          <IconClock size={12} />
                          Grossed at:
                        </Group>
                      </Text>
                      <Text size="sm">
                        {dayjs(specimen.grossedAt).format("MMM D, YYYY h:mm A")}
                      </Text>
                    </Group>
                  )}

                  {/* Gross Description */}
                  {specimen.gross && (
                    <div>
                      <Text size="sm" fw={500} mb={4}>
                        Gross Description:
                      </Text>
                      <Paper p="sm" bg="gray.0" radius="sm">
                        {/* eslint-disable react/no-danger */}
                        <div
                          style={{
                            fontSize: "var(--mantine-font-size-sm)",
                            lineHeight: 1.5,
                            color: "var(--mantine-color-gray-7)",
                          }}
                          dangerouslySetInnerHTML={{
                            __html: resolveGrossVariables(specimen.gross),
                          }}
                        />
                        {/* eslint-enable react/no-danger */}
                      </Paper>
                    </div>
                  )}
                </Stack>

                {/* Blocks */}
                {blocks.map((block) => (
                  <Paper key={block.id} p="sm" withBorder>
                    <Group gap="xs" mb="xs">
                      <IconBox size={16} />
                      <Text size="sm" fw={500}>
                        Block {block.number}
                      </Text>
                      {block.isAdditional && (
                        <Badge size="xs" color="orange" variant="light">
                          Additional
                        </Badge>
                      )}
                    </Group>

                    {/* Slides */}
                    {block.slides && block.slides.length > 0 ? (
                      <Stack gap="xs" ml="md">
                        {block.slides.map((slide) => (
                          <Group key={slide.id} gap="xs">
                            <IconMicroscope size={14} />
                            <Text size="sm">Slide {slide.number}</Text>
                            {slide.stain && (
                              <Badge size="xs" variant="outline">
                                {slide.stain.code}
                              </Badge>
                            )}
                            <Badge
                              size="xs"
                              color={slide.isCompleted ? "green" : "gray"}
                              variant="light"
                            >
                              {slide.isCompleted ? "Completed" : "Pending"}
                            </Badge>
                            {slide.isControlSlide && (
                              <Badge size="xs" color="violet" variant="filled">
                                Control Slide for{" "}
                                {dayjs(slide.createdAt).year() !==
                                dayjs().year()
                                  ? dayjs(slide.createdAt).format("MMM D, YYYY")
                                  : dayjs(slide.createdAt).format("MMM D")}
                              </Badge>
                            )}
                          </Group>
                        ))}
                      </Stack>
                    ) : (
                      <Text size="sm" c="dimmed" ml="md">
                        No slides
                      </Text>
                    )}
                  </Paper>
                ))}

                {blocks.length === 0 && (
                  <Text size="sm" c="dimmed">
                    No blocks created yet
                  </Text>
                )}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
}
